import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  ApiWorkflowExecutionBlockStatus,
  ApiWorkflowExecutionListItem,
  ApiWorkflowExecutionStatus,
  getApiWorkflowExecutionBlockStatusTitle,
  getApiWorkflowExecutionStatusTitle,
} from "@/types/workflows/workflow-execution.types.ts";
import { Stack, Tag, Tooltip, TooltipContent, TooltipTrigger } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export const useWorkflowExecutions = () => {
  const { t } = useTranslation();
  const { renderDateAndTime } = useFormatter();

  const getWorkflowExecutionStatusCell = (execution: ApiWorkflowExecutionListItem) => {
    if (execution.status !== ApiWorkflowExecutionStatus.pending) {
      return <Stack items={"start"}>{getWorkflowExecutionStatusTag(execution.status)}</Stack>;
    }

    const hint = execution.datetime_not_before_at
      ? t("workflow:execution_will_start_not_before", {
          datetime: renderDateAndTime(execution.datetime_not_before_at),
        })
      : t("workflow:execution_will_start_soon");

    return (
      <Tooltip>
        <TooltipTrigger asChild={true}>
          <span className={"inline-flex w-fit cursor-help"}>
            {getWorkflowExecutionStatusTag(execution.status)}
          </span>
        </TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
    );
  };

  const getWorkflowExecutionStatusTag = (status: ApiWorkflowExecutionStatus) => {
    const statusTitle = getApiWorkflowExecutionStatusTitle(status);

    switch (status) {
      case ApiWorkflowExecutionStatus.pending:
        return <Tag variant={"secondary"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionStatus.running:
        return <Tag variant={"positive"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionStatus.completed:
        return <Tag variant={"primary"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionStatus.failed:
        return <Tag variant={"danger"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionStatus.cancelled:
        return <Tag variant={"secondary"}>{statusTitle}</Tag>;
    }
  };

  const getWorkflowExecutionBlockStatusTag = (status: ApiWorkflowExecutionBlockStatus) => {
    const statusTitle = getApiWorkflowExecutionBlockStatusTitle(status);

    switch (status) {
      case ApiWorkflowExecutionBlockStatus.pending:
        return <Tag variant={"primary"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionBlockStatus.running:
        return <Tag variant={"positive"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionBlockStatus.waiting_for_external:
        return <Tag variant={"secondary"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionBlockStatus.scheduled:
        return <Tag variant={"new"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionBlockStatus.skipped_by_condition:
        return <Tag variant={"warning"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionBlockStatus.completed:
        return <Tag variant={"secondary"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionBlockStatus.failed:
        return <Tag variant={"danger"}>{statusTitle}</Tag>;
      case ApiWorkflowExecutionBlockStatus.cancelled:
        return <Tag variant={"secondary"}>{statusTitle}</Tag>;
    }
  };

  return {
    getWorkflowExecutionStatusTag,
    getWorkflowExecutionStatusCell,
    getWorkflowExecutionBlockStatusTag,
  };
};
